<template>
  <Modal ref="infoModal" title="Detalhes da Turma" @cancel="closeInfoModal" data-testid="info-modal-root">
    <Tabs v-if="turma" :options="[
      'Dados básicos',
      'Alunos regulares',
      'Alunos reposição',
      'Aulas',
      'Presenças',
    ]" @handle:change="onTabChanged" data-testid="info-tabs">
      <div slot="Dados básicos" class="basic-container" data-testid="tab-dados-basicos">
        <div class="basic-header" v-if="turma.turma.id_agrupamento" data-testid="basic-header">
          <div class="header-item">
            <strong>Agrupamento: </strong>
            <span>{{ turma.turma.id_agrupamento }}</span>
          </div>

          <div class="header-item">
            <strong>Data de Início: </strong>
            <span>{{ utils.formatDate(turma.turma.data_inicio) }}</span>
          </div>

          <div class="header-item">
            <strong>Data de Finalização: </strong>
            <span>{{ utils.formatDate(turma.turma.data_fim) }}</span>
          </div>
        </div>

        <div class="basic-content" data-testid="basic-actions">
          <Button text="Cancelar turma" color="danger" :fill="false" :disabled="loadingActions"
            @handle:click="cancelarTurma" data-testid="btn-cancelar-turma">
            <Cancel :size="19" />
          </Button>

          <Button text="Atualizar turma" color="secondary" :fill="false" :disabled="loadingActions"
            @handle:click="atualizarBiometriaVirtual" data-testid="btn-atualizar-turma">
            <Autorenew :size="19" />
          </Button>

          <Button text="Enviar biometria" color="primary" :fill="false" :disabled="loadingActions"
            @handle:click="enviarBiometriaVirtual" data-testid="btn-enviar-biometria">
            <SendVariant :size="19" />
          </Button>
        </div>
      </div>

      <div slot="Alunos regulares" data-testid="tab-alunos-regulares">
        <Table :headers="['Nome', 'CPF', 'Renach']" v-if="turma" data-testid="table-alunos-regulares">
          <tr v-if="!turma.alunos_regulares.length" data-testid="no-alunos-regulares">
            <td colspan="3">
              <div class="text-center" data-testid="no-alunos-regulares-text">
                Nenhum aluno regular foi registrado nessa turma.
              </div>
            </td>
          </tr>
          <tr v-for="aluno in turma.alunos_regulares" :key="aluno.id_aluno" :data-testid="'aluno-regular-row-' + aluno.id_aluno">
            <td>{{ aluno.nome }}</td>
            <td>{{ utils.formatarCPF(aluno.cpf) }}</td>
            <td>{{ aluno.renach }}</td>
          </tr>
        </Table>
      </div>

      <div slot="Alunos reposição" data-testid="tab-alunos-reposicao">
        <Table :headers="['Nome', 'CPF', 'Renach']" v-if="turma" data-testid="table-alunos-reposicao">
          <tr v-if="!turma.alunos_reposicao.length" data-testid="no-alunos-reposicao">
            <td colspan="3">
              <div class="text-center" data-testid="no-alunos-reposicao-text">
                Nenhum aluno reposição foi registrado nessa turma.
              </div>
            </td>
          </tr>
          <tr v-for="aluno in turma.alunos_reposicao" :key="aluno.id_aluno" :data-testid="'aluno-reposicao-row-' + aluno.id_aluno">
            <td>{{ aluno.nome }}</td>
            <td>{{ utils.formatarCPF(aluno.cpf) }}</td>
            <td>{{ aluno.renach }}</td>
          </tr>
        </Table>
      </div>

      <div slot="Aulas" data-testid="tab-aulas">
        <Table :headers="aulasHeaders" v-if="turma" data-testid="table-aulas">
          <tr v-if="!turma.aulas.length" data-testid="no-aulas">
            <td colspan="6">
              <div class="text-center">
                Nenhum Registro encontrado
              </div>
            </td>
          </tr>
          <tr v-for="aula in turma.aulas" :key="aula.id_aula_teorica" :data-testid="'aula-row-' + aula.id_aula_teorica">
            <td>{{ turma.instrutor.nome }}</td>
            <td>{{ aula.materia.titulo }}</td>
            <td>{{ utils.formatDate(aula.inicio) }}</td>
            <td>{{ utils.formatDate(aula.fim) }}</td>
            <td>{{ aula.duracao_minutos }} minutos</td>
            <td v-if="!turma.turma.virtual">
              <div class="actions">
                <IconButton @handle:click="loadDataFotosClass(aula)" color="primary" title="Visualizar foto"
                  :disabled="loadingActions" data-testid="btn-visualizar-foto-aula">
                  <ImageArea :size="19" />
                </IconButton>
              </div>
            </td>
          </tr>
        </Table>
      </div>

      <div slot="Presenças" class="presences" data-testid="tab-presencas">
        <StatusLegend :statusOptions="statusOptions" data-testid="status-legend" />

        <Table :headers="presencasHeaders" v-if="turma" data-testid="table-presencas">
          <tr v-if="!presencas.length" data-testid="no-presencas">
            <td colspan="6">
              <div class="text-center">
                Nenhum Registro encontrado
              </div>
            </td>
          </tr>
          <tr v-for="(presenca, index) in presencas" :key="index" :data-testid="'presenca-row-' + index">
            <td>{{ presenca.nome }}</td>
            <td>{{ utils.formatarCPF(presenca.cpf) }}</td>
            <td>{{ presenca.entidade }}</td>
            <td v-if="presenca.presente === 'ENTRADA' || presenca.presente === 'INTERVALO'">
              <BadgeCircle color="yellow" />
            </td>
            <td v-else-if="presenca.presente === 'SAIDA'">
              <BadgeCircle color="green" />
            </td>
            <td v-else>
              <BadgeCircle color="red" />
            </td>
            <td v-if="!presenca.presente">
              SEM PRESENÇA
            </td>
            <td v-else>
              <span v-if="presenca.entidade === 'INSTRUTOR'">
                INSTRUTOR
              </span>
              <span v-else-if="presenca.status === null">
                AGUARDANDO ENVIO
              </span>
              <span v-else>
                {{ presenca.presencas[0].status }}
              </span>
            </td>
            <td>
              <span v-if="!presenca.presente">
                AUSENTE
              </span>
              <IconButton v-else @handle:click="getPresencas(presenca)" color="primary" title="Visualizar fotos"
                :disabled="loadingActions" data-testid="btn-visualizar-fotos-presenca">
                <ImageArea :size="19" />
              </IconButton>
            </td>
            <td v-if="presenca.presencas && presenca.presencas.length > 0">
              {{ presenca.presencas[0].dispositivo }}
            </td>
            <td v-else>
              -
            </td>
            <td v-if="idRole !== 9">
              <div class="actions">
                <IconButton @handle:click="sendPresenca(presenca)" color="primary" title="Adicionar presença"
                  :disabled="loadingActions || !(!presenca.presente || presenca.presente === 'ENTRADA' || presenca.presente === 'INTERVALO')" data-testid="btn-adicionar-presenca">
                  <Plus :size="19" />
                </IconButton>
              </div>
            </td>
          </tr>
        </Table>
      </div>
    </Tabs>

    <Modal ref="aulasFotos" title="Fotos da turma" data-testid="modal-aulas-fotos">
      <div data-testid="aulas-fotos-container">
        <div v-if="!aulasFotos.length" data-testid="no-aulas-fotos">
          <div class="text-center" data-testid="no-aulas-fotos-text">
            Nenhuma foto foi registrada nessa turma.
          </div>
        </div>

        <div v-else data-testid="aulas-fotos-list">
          <div v-for="(foto, index) in aulasFotos" :key="index" :data-testid="'aulas-foto-' + index">
            <img :src="`data:image/jpeg;base64, ${foto}`" alt="Foto da turma" class="img-fluid" />
          </div>
        </div>
      </div>
    </Modal>

    <Modal ref="presencaFotos" title="Presenças" data-testid="modal-presenca-fotos">
      <div class="presencas-container" v-if="presencasFotos.length || presencasEntidade.length" data-testid="presenca-fotos-container">
        <table class="presencas-table" data-testid="presenca-fotos-table">
          <thead>
            <tr class="presencas-table-header">
              <th>Foto</th>
              <th>Tipo</th>
              <th>Horário</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(presenca, index) in presencasEntidade" :key="index" class="presencas-table-content" :data-testid="'presenca-foto-row-' + index">
              <td>
                <img width="100" :src="'data:image/jpg;base64, ' + presenca.foto" alt="Foto">
              </td>
              <td v-show="presenca.tipo == 0">Entrada</td>
              <td v-show="presenca.tipo == 1" class="red">Saída</td>
              <td v-show="presenca.tipo == 2">Intervalo</td>
              <td>{{ utils.formatDate(presenca.created_at) }}</td>
            </tr>
          </tbody>
        </table>

        <div class="presencas-photos-container" data-testid="presencas-photos-container">
          <h3>Fotos aulas teóricas</h3>

          <div class="photos">
            <span v-if="!presencasFotos.length" data-testid="no-presencas-fotos">Sem fotos teóricas</span>
            <img v-for="(foto, index) in presencasFotos" :key="index" :src="'data:image/jpg;base64, ' + foto.foto"
              alt="Fotos teóricas" :data-testid="'presenca-foto-teorica-' + index">
          </div>
        </div>
      </div>
    </Modal>

    <Modal ref="givePresenca" :title="selectedPresenca ? selectedPresenca.nome : ''" @cancel="clearGivePresenca" data-testid="modal-give-presenca">
      <div class="give-presenca-modal" data-testid="give-presenca-container">
        <Table :headers="['Entrada', 'Intervalo', 'Saída']" v-if="selectedPresenca" data-testid="table-give-presenca">
          <tr>
            <td>
              <input type="time" v-model="setEntrada" data-testid="input-entrada" />
            </td>
            <td>
              <span v-show="this.selectedPresenca.entidade === 'ALUNO'">
                <input type="time" v-model="setIntervalo" data-testid="input-intervalo" />
              </span>
              <span v-show="this.selectedPresenca.entidade === 'INSTRUTOR'">
                <input type="time" disabled />
              </span>
            </td>
            <td>
              <input type="time" v-model="setSaida" data-testid="input-saida" />
            </td>
          </tr>
          <tr>
            <td>
              <div v-if="selectedImgEntrada == null">
                <label class="badge badge-warning" for="entradaImg" data-testid="label-entrada">Carregar Foto</label>
                <input type="file" id="entradaImg" accept="image/jpeg, image/jpg, image/png" @change="setImgEnter" data-testid="file-entrada">
              </div>
              <div v-else>
                <img :src="selectedImgEntrada" for="entradaImg" class='w-50'>
                <span class="fa fa-close" title="Remover Foto" @click="clearImg('e')" data-testid="btn-remover-foto-entrada"
                  style="position: absolute; justify-content: center; align-items: center; cursor:pointer">
                </span>
              </div>
            </td>
            <td></td>
            <td>
              <div v-if="selectedImgSaida == null">
                <label class="badge badge-warning" for="saidaImg" data-testid="label-saida">Carregar Foto</label>
                <input type="file" id="saidaImg" accept="image/jpeg, image/jpg, image/png" @change="setImgExit" data-testid="file-saida">
              </div>
              <div v-else>
                <img :src="selectedImgSaida" for="entradaImg" class='w-50'>
                <span class="fa fa-close" title="Remover Foto" @click="clearImg('s')" data-testid="btn-remover-foto-saida"
                  style="position: absolute; justify-content: center; align-items: center; cursor:pointer">
                </span>
              </div>
            </td>
          </tr>
        </Table>

        <div class="buttons">
          <LoadingSpinner v-if="loadingActions" data-testid="loading-actions" :size="50" />

          <Button v-else type="primary" @handle:click="marcarPresenca" text="Confirmar presença"
            :disabled="loadingActions" data-testid="btn-confirmar-presenca"></Button>
        </div>
      </div>
    </Modal>
  </Modal>
</template>

<script>
import utils from '../../../services/utils'
import Modal from 'core/Modal'
import Tabs from 'core/Tabs'
import Button from 'core/Button'
import Table from 'core/Table'
import StatusLegend from 'core/StatusLegend'
import IconButton from 'core/IconButton'
import BadgeCircle from 'core/BadgeCircle'
import LoadingSpinner from 'core/LoadingSpinner'
import SendVariant from 'icons/SendVariant'
import Cancel from 'icons/Cancel'
import Autorenew from 'icons/Autorenew'
import ImageArea from 'icons/ImageArea'
import Plus from 'icons/Plus'

export default {
  name: 'TurmasTeoricasInfoModal',
  components: {
    Modal,
    Tabs,
    Button,
    Table,
    IconButton,
    StatusLegend,
    BadgeCircle,
    LoadingSpinner,

    SendVariant,
    Cancel,
    Autorenew,
    ImageArea,
    Plus,
  },
  data() {
    return {
      idRole: Number(localStorage.id_role),
      utils: utils,
      schema: null,
      turma: null,
      selectedPresenca: null,
      setEntrada: '',
      setIntervalo: '',
      setSaida: '',
      selectedImgEntrada: null,
      selectedImgSaida: null,
      presencas: [],
      aulasFotos: [],
      statusOptions: [
        { legend: 'Ausente', color: 'red' },
        { legend: 'Presente (entrada / saída)', color: 'yellow' },
        { legend: 'Presente', color: 'green' },
      ],
      presencasFotos: [],
      presencasEntidade: [],
      loadingActions: false,
    }
  },
  computed: {
    aulasHeaders() {
      if (!this.turma.turma.virtual) {
        return ['Instrutor', 'Matéria', 'Início', 'Fim', 'Duração', 'Fotos']
      }

      return ['Instrutor', 'Matéria', 'Início', 'Fim', 'Duração']
    },
    presencasHeaders() {
      const headers = ['Nome', 'CPF', 'Entidade', 'Presença', 'Status', 'Foto Presença', 'Dispositivo']

      if (this.idRole !== 9) {
        headers.push('Ações')
      }

      return headers
    },
  },
  methods: {
    async open(schema, turma) {
      this.schema = schema

      const params = {
        autoescola: schema,
      }

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/aulas/teoricas/turmas/consultar/id/${turma.id_turma_teorica}`, { params })
        .then(res => {
          const dados = res.body.dados

          this.turma = dados
          this.loadPresencas(dados)
        })
        .catch(err => {
          console.log(err)
        })

      this.$refs.infoModal.open()
    },
    loadPresencas(dados) {
      if (dados.alunos_reposicao.length) {
        dados.alunos_reposicao.map(aluno => {
          this.setPresenca('ALUNO', aluno)
        })
      }

      if (dados.alunos_regulares.length) {
        dados.alunos_regulares.map(aluno => {
          this.setPresenca('ALUNO', aluno)
        })
      }

      if (dados.instrutor) {
        this.setPresenca('INSTRUTOR', dados.instrutor)
      }

      let entrada, intervalo, saida

      this.presencas.map(entidade => {
        if (entidade.presencas.length) {
          entrada = false
          intervalo = false
          saida = false

          entidade.presencas.map(presenca => {
            if (presenca.tipo === '0' || presenca.tipo === 'INICIO') {
              entrada = true
            } else if (presenca.tipo === '2' || presenca.tipo === 'INTERVALO') {
              intervalo = true
            } else if (presenca.tipo === '1' || presenca.tipo === 'FIM') {
              saida = true
            }

            if (entrada && saida) {
              this.presencas.forEach(element => {
                if (element.id === presenca.id) {
                  element.presente = 'SAIDA'
                }
              });
            }
            else if (entrada && intervalo) {
              this.presencas.forEach(element => {
                if (element.id === presenca.id) {
                  element.presente = 'INTERVALO'
                }
              });
            }
            else if (entrada && !saida) {
              this.presencas.forEach(element => {
                if (element.id === presenca.id) {
                  element.presente = 'ENTRADA'
                }
              });
            }
          })
        }
      })
    },
    setPresenca(type, model) {
      const id = type === 'ALUNO' ? model.id_aluno : model.id_instrutor

      this.presencas.push({
        id: id,
        nome: model.nome,
        cpf: model.cpf,
        entidade: type,
        status: model.status,
        presencas: model.presencas ? model.presencas : null,
        dispositivo: null,
      })
    },
    closeInfoModal() {
      this.$refs.infoModal.close()

      this.schema = null
      this.turma = null
      this.presencas = []
    },
    onTabChanged(tabName) {
      console.log(tabName)
    },
    async cancelarTurma() {
      this.loadingActions = true

      const params = {
        schema: this.schema
      }

      await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/turmas/cancelar/${this.turma.turma.id_agrupamento}`, params)
        .then(res => {
          this.$toasted.show('Turma cancelada com sucesso', {
            position: 'top-right',
          });
        })
        .catch(err => {
          this.$toasted.show(err.body.mensagem, {
            position: 'top-right',
          });
          this.$toasted.show(err.body.dados, {
            position: 'top-right',
          });
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    async atualizarBiometriaVirtual() {
      this.loadingActions = true

      const params = {
        schema: this.schema
      }

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/getran/atualizar/turma/${this.turma.turma.id_agrupamento}/biometria`, { params })
        .then(res => {
          this.$toasted.show('Biometria virtual atualizada com sucesso', {
            position: 'top-right',
          });

          this.closeInfoModal()
        })
        .catch(err => {
          this.$toasted.show(err.body.mensagem, {
            position: 'top-right',
          });
          this.$toasted.show(err.body.dados, {
            position: 'top-right',
          });
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    async enviarBiometriaVirtual() {
      this.loadingActions = true

      const params = {
        schema: this.schema
      }

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/getran/enviar/turma/${this.turma.turma.id_agrupamento}/biometria`, { params })
        .then(res => {
          this.$toasted.show('Biometria virtual atualizada com sucesso', {
            position: 'top-right',
          });

          this.closeInfoModal()
        })
        .catch(err => {
          this.$toasted.show(err.body.mensagem, {
            position: 'top-right',
          });
          this.$toasted.show(err.body.dados, {
            position: 'top-right',
          });
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    async loadDataFotosClass(aula) {
      this.loadingActions = true

      const params = {
        turma: aula.id_turma_teorica,
        aula: aula.id_aula_teorica,
        schema: this.schema
      }

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/aulas/teoricas/turmas/consultar/aulas-fotos`, { params })
        .then(res => {
          this.aulasFotos = res.body.dados
        })
        .catch(err => {
          this.$toasted.show(err.body.mensagem, {
            position: 'top-right',
          });
        })
        .finally(() => {
          this.loadingActions = false
        })

      this.$refs.aulasFotos.open()
    },
    async getPresencas(presenca) {
      this.loadingActions = true

      const params = {
        schema: this.schema,
        id: presenca.id,
        entidade: presenca.entidade,
      }

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/aulas/teoricas/turmas/consultar/fotos/id/${this.turma.turma.id_turma_teorica}`, { params })
        .then(res => {
          const dados = res.body.dados

          this.presencasFotos = dados.fotos
          this.presencasEntidade = dados.presencas
        })
        .catch(err => {
          this.$toasted.show(err.body.mensagem, {
            position: 'top-right',
          });
        })
        .finally(() => {
          this.loadingActions = false
        })

      this.$refs.presencaFotos.open()
    },
    async sendPresenca(presenca) {
      this.selectedPresenca = presenca

      this.selectedImgEntrada = null,
      this.selectedImgSaida = null,
      this.$refs.givePresenca.open()
    },
    setImgEnter(event) {
      const files = event.target.files
      const fileReader = new FileReader()

      fileReader.readAsDataURL(files[0])
      fileReader.addEventListener('load', () => {
        this.selectedImgEntrada = fileReader.result
      })
    },
    setImgExit(event) {
      const files = event.target.files
      const fileReader = new FileReader()

      fileReader.readAsDataURL(files[0])
      fileReader.addEventListener('load', () => {
        this.selectedImgSaida = fileReader.result
      })
    },
    clearImg(type) {
      if (type === 'e') {
        this.selectedImgEntrada = null
      } else {
        this.selectedImgSaida = null
      }
    },
    async marcarPresenca() {
      this.loadingActions = true

      const params = {
        schema: this.schema,
        entidade: this.selectedPresenca.entidade,
        id_turma: this.turma.turma.id_turma_teorica,
        id_entidade: this.selectedPresenca.id,
        entrada: this.setEntrada,
        intervalo: this.setIntervalo,
        saida: this.setSaida,
        fotoEntrada: this.selectedImgEntrada,
        fotoSaida: this.selectedImgSaida,
        id_turma_teorica: this.turma.turma.id_turma_teorica,
      }

      await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/aulas/teoricas/turmas/presenca`, params)
        .then(async () => {
          this.presencas = []

          await this.open(this.schema, this.turma.turma)
          
          this.$toasted.show('Presença marcada com sucesso', {
            position: 'top-right',
          });
        })
        .catch(err => {
          this.$toasted.show(err.body.mensagem, {
            position: 'top-right',
          });
        })
        .finally(() => {
          this.loadingActions = false

          this.$refs.givePresenca.close()
        })
    },
    clearGivePresenca() {
      this.selectedPresenca = null
      this.selectedImgEntrada = null
      this.selectedImgSaida = null
    }
  },
}
</script>

<style scoped lang="scss">
input[type="file"] {
  display: none;
}

.basic-container {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .basic-header {
    border: 1px solid #d9d9d9;
    border-radius: 12px;
    padding: 1rem;


    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    .header-item {
      strong {
        font-size: 1rem;
        font-weight: 700;
      }

      span {
        font-size: 1rem;
        font-weight: 500;
      }
    }
  }

  .basic-content {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 1rem;
  }
}

.presences {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.presencas-container {
  width: 50vw;
  display: flex;
  flex-direction: column;
  gap: 2.0625rem;

  .presencas-table {
    tr {
      border: none;
    }

    .presencas-table-header {
      >th {
        padding: 0.5rem 0.5rem 2rem;
        font-size: 1rem;
        font-weight: 700;
        text-align: center;
        color: #1C2D5A;
      }
    }

    .presencas-table-content {
      >td {
        font-size: 1rem;
        font-weight: 700;
        text-align: center;
        color: #1C2D5A;

        &.red {
          color: #E91D23;
        }
      }

      img {
        width: 10.5rem;
        height: 10.5625rem;
        border-radius: 5.74px;
        border: 0.57px solid #A8A8A8;
        object-fit: contain;
      }
    }
  }

  .presencas-photos-container {
    display: flex;
    flex-direction: column;
    gap: 2.0625rem;

    h3 {
      margin: 0;
      display: flex;
      flex-direction: column;
      font-size: 1.5rem;
      font-weight: 700;
      text-align: left;
      color: #202224;
    }

    .photos {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 2.0625rem;

      >img {
        width: 5.375rem;
        height: 5.375rem;
        border-radius: 2.93px;
        border: 0.29px solid #A8A8A8;
      }
    }
  }

  .actions {
    display: flex;
    align-items: center;

    div {
      padding: 0 0.5rem;
      display: flex;
      justify-content: center;

      &:not(:first-child) {
        border-left: 1px solid #ECECEC;
      }

      &:not(:last-child) {
        border-right: 1px solid #ECECEC;
      }
    }
  }
}

.give-presenca-modal {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 1rem;
  }
}
</style>
